import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Typography,
  Divider,
  Stack,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { abyssColors } from '@/theme/colors';
import { ReportInternalType, ReportStatus } from '@/__generated__/graphql';
import { ReportsList } from './ReportsList';
import { useFilteredReportsByType } from '../hooks/useFilteredReportsByType';

type ReportGroup = {
  groupId: string;
  groupName: string;
  count: number;
};

type ReportGroupsProps = {
  reportGroup: ReportGroup;
  selectedReportType: ReportInternalType;
  status: ReportStatus | 'ALL';
  searchTerm: string | undefined;
  selectedReportIds: string[];
  setSelectedReportIds: React.Dispatch<React.SetStateAction<string[]>>;
};

export const ReportGroups = ({
  reportGroup,
  selectedReportType,
  status,
  searchTerm,
  selectedReportIds,
  setSelectedReportIds,
}: ReportGroupsProps) => {
  const { reports, loading } = useFilteredReportsByType(
    selectedReportType,
    status,
    searchTerm,
    reportGroup.groupId,
    reportGroup.groupName
  );

  const groupReportIds = reports.map((r) => r?.id).filter(Boolean) as string[];
  const selectedInGroup = groupReportIds.filter((id) => selectedReportIds.includes(id));
  const allSelected = groupReportIds.length > 0 && selectedInGroup.length === groupReportIds.length;
  const someSelected = selectedInGroup.length > 0;

  const handleGroupCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.target.checked) {
      setSelectedReportIds((prev) => [...new Set([...prev, ...groupReportIds])]);
    } else {
      setSelectedReportIds((prev) => prev.filter((id) => !groupReportIds.includes(id)));
    }
  };

  return (
    <>
      <Accordion
        disableGutters
        elevation={0}
        sx={{
          boxShadow: 'none',
          pt: 1,
          pb: 1,
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          aria-controls={`${reportGroup.groupId}-content`}
          id={`${reportGroup.groupId}-header`}
          expandIcon={<ExpandMoreIcon />}
          sx={{
            flexDirection: 'row-reverse',
            px: 0,
            py: 1,
            '& .MuiAccordionSummary-expandIconWrapper': {
              transform: 'rotate(-90deg)',
              transition: 'transform 0.2s ease-in-out',
              '&.Mui-expanded': {
                transform: 'rotate(0deg)',
              },
              marginRight: 1,
            },
            '& .MuiSvgIcon-root': {
              color: abyssColors.primary[500],
            },
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
            <Stack direction="row" alignItems="center" gap={1} flex={1}>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={handleGroupCheckboxChange}
                onClick={(e) => e.stopPropagation()}
                size="medium"
                sx={{ py: 0, px: 0 }}
                disableRipple
              />
              <Typography
                variant="h5"
                sx={{ color: abyssColors.primary[700], fontWeight: 500, fontSize: '1.4rem' }}
              >
                {reportGroup.groupName}
              </Typography>
            </Stack>
            <Typography
              variant="h4"
              sx={{ color: abyssColors.primary[700], fontWeight: 500, fontSize: '2.0rem' }}
            >
              {loading ? <CircularProgress size={20} /> : String(reports.length)}
            </Typography>
          </Stack>
        </AccordionSummary>

        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <ReportsList
            selectedReportType={selectedReportType}
            status={status}
            searchTerm={searchTerm}
            groupId={reportGroup.groupId}
            groupName={reportGroup.groupName}
            selectedReportIds={selectedReportIds}
            setSelectedReportIds={setSelectedReportIds}
          />
        </AccordionDetails>
      </Accordion>
      <Divider />
    </>
  );
};
